/**
 * Breadcrumbs Component
 * แสดง navigation path: Home > Section > Page
 * สำหรับ TC-NAV-003
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation();

  // Auto-generate breadcrumbs from URL if items not provided
  const breadcrumbs = items || generateBreadcrumbs(location.pathname);

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex items-center space-x-2 text-sm">
        {/* Home link */}
        <li>
          <Link
            to="/"
            className="flex items-center text-neutral-600 hover:text-primary-600 transition-colors"
            aria-label="หน้าแรก"
          >
            <HomeIcon className="w-4 h-4" />
          </Link>
        </li>

        {/* Breadcrumb items */}
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <React.Fragment key={index}>
              <li>
                <ChevronRightIcon className="w-4 h-4 text-neutral-400" />
              </li>
              <li>
                {isLast || !item.path ? (
                  // Current page (not clickable)
                  <span className="font-medium text-neutral-900" aria-current="page">
                    {item.label}
                  </span>
                ) : (
                  // Clickable link
                  <Link
                    to={item.path}
                    className="text-neutral-600 hover:text-primary-600 transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Auto-generate breadcrumbs from pathname
 */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const pathMap: Record<string, BreadcrumbItem[]> = {
    '/my-companies': [{ label: 'บริษัทของฉัน' }],
    '/settings': [{ label: 'การตั้งค่า' }],
    '/settings/profile': [
      { label: 'การตั้งค่า', path: '/settings' },
      { label: 'ข้อมูลส่วนตัว' },
    ],
    '/settings/password': [
      { label: 'การตั้งค่า', path: '/settings' },
      { label: 'เปลี่ยนรหัสผ่าน' },
    ],
    '/ownership/claims': [{ label: 'คำขอ Claim' }],
    '/feedback': [{ label: 'ส่งความคิดเห็น' }],
    '/feedback/my-feedbacks': [
      { label: 'ความคิดเห็น', path: '/feedback' },
      { label: 'ความคิดเห็นของฉัน' },
    ],
    '/search': [{ label: 'ค้นหาบริษัท' }],
    '/about': [{ label: 'เกี่ยวกับเรา' }],
    '/contact': [{ label: 'ติดต่อเรา' }],
  };

  // Check for exact match
  if (pathMap[pathname]) {
    return pathMap[pathname];
  }

  // Check for company detail page pattern: /companies/:slug
  if (pathname.startsWith('/companies/')) {
    return [
      { label: 'ค้นหาบริษัท', path: '/search' },
      { label: 'รายละเอียดบริษัท' },
    ];
  }

  // Check for admin routes
  if (pathname.startsWith('/admin')) {
    const adminPathMap: Record<string, BreadcrumbItem[]> = {
      '/admin/dashboard': [{ label: 'แดชบอร์ด' }],
      '/admin/companies': [{ label: 'จัดการบริษัท' }],
      '/admin/users': [{ label: 'จัดการผู้ใช้' }],
      '/admin/claims': [{ label: 'คำขอ Claim' }],
      '/admin/change-requests': [{ label: 'คำขอเปลี่ยนแปลง' }],
      '/admin/service-types': [{ label: 'ประเภทบริการ' }],
      '/admin/provinces': [{ label: 'จังหวัด' }],
    };

    if (adminPathMap[pathname]) {
      return [{ label: 'แอดมิน', path: '/admin/dashboard' }, ...adminPathMap[pathname]];
    }
  }

  // Default: return empty (no breadcrumbs)
  return [];
}

export default Breadcrumbs;
